// goals are sticky notes with a progress bar. shared by the server (which keeps them tidy) and the app (which draws them)

export const GOAL_COLOURS = ["yellow", "pink", "mint", "peach", "lilac", "sky", "red", "slate"];

export interface Goal {
  id: string;
  title: string;
  note: string;
  colour: string;
  // how far along, out of target. a goal with no number of its own counts to 100 as a percent
  progress: number;
  target: number;
  // "%" or what's being counted, "pages", "sketches"
  unit: string;
  done: boolean;
  doneAt: string;
  createdAt: string;
  updatedAt: string;
}

export type GoalDraft = Partial<Pick<Goal, "id" | "title" | "note" | "colour" | "progress" | "target" | "unit">>;

export const MAX_GOALS = 300;
export const MAX_TARGET = 1_000_000;

// one tap of + or -: single steps for small counts, twentieths for big ones
export function goalStep(goal: Pick<Goal, "target">): number {
  return goal.target <= 20 ? 1 : Math.max(1, Math.round(goal.target / 20));
}

export function goalShare(goal: Pick<Goal, "progress" | "target">): number {
  return goal.target > 0 ? Math.min(1, Math.max(0, goal.progress / goal.target)) : 0;
}

// "40%" or "3 / 10 pages"
export function goalReadout(goal: Pick<Goal, "progress" | "target" | "unit">): string {
  if (goal.unit === "%" && goal.target === 100) return `${Math.round(goal.progress)}%`;
  return `${goal.progress} / ${goal.target}${goal.unit && goal.unit !== "%" ? ` ${goal.unit}` : ""}`;
}

// open ones newest first so they don't jump about while you tap +, then the crossed out ones by when they were done
export function sortGoals(goals: Goal[]): Goal[] {
  const open = goals.filter((goal) => !goal.done).sort((first, second) => second.createdAt.localeCompare(first.createdAt));
  const done = goals.filter((goal) => goal.done).sort((first, second) => second.doneAt.localeCompare(first.doneAt));
  return [...open, ...done];
}
